import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  GENERIC_ERROR_MESSAGE,
  HTTP_ERROR_MESSAGES,
  VALIDATION_ERROR_TRANSLATIONS,
} from './error-messages';
import { ApiError } from '@seatix/shared-domain-utils';

/**
 * Service to extract and format error messages from HTTP responses
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorExtractorService {
  /**
   * Extracts a user-friendly error message from an HTTP error response
   *
   * @param error The HTTP error response
   * @returns A formatted error message to display to the user
   */
  extractErrorMessage(error: HttpErrorResponse): string {
    // If there is a network error (no server response)
    if (
      typeof ErrorEvent !== 'undefined' &&
      error.error instanceof ErrorEvent
    ) {
      return 'Connection error. Please check your internet connection.';
    }

    try {
      // Handle 422 validation errors with special processing
      if (error.status === 422) {
        const validationMessage = this.#extractValidationError(error);
        if (validationMessage) {
          return validationMessage;
        }
      }

      const apiError = this.#parseApiError(error);

      // If we could extract a message from the API, use it
      if (apiError?.error) {
        return this.#formatErrorMessage(apiError.message);
      }

      // If there is no specific message, use a predefined message according to the HTTP status code
      if (error.status in HTTP_ERROR_MESSAGES) {
        return HTTP_ERROR_MESSAGES[error.status];
      }

      // If nothing works, use a generic message
      return GENERIC_ERROR_MESSAGE;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return GENERIC_ERROR_MESSAGE;
    }
  }

  /**
   * Tries to parse the response body as an ApiError
   *
   * @param error The HTTP error response
   * @returns An ApiError object or null if it could not be parsed
   */
  #parseApiError(error: HttpErrorResponse): ApiError | null {
    if (!error.error) {
      return null;
    }

    // If it is already an object, check if it has the expected structure
    if (typeof error.error === 'object') {
      // Domain exceptions put a discriminator CODE in `error` (a string, e.g.
      // ITEM_IN_OTHER_CART) and the user-facing copy in the top-level `message`.
      if ('error' in error.error) {
        const nested = error.error.error;
        if (nested && typeof nested === 'object' && 'message' in nested) {
          return nested as ApiError;
        }
      }

      // Check if the object has the message property
      if ('message' in error.error) {
        return error.error as ApiError;
      }
    }

    // If it is a string, try to parse it as JSON
    if (typeof error.error === 'string') {
      try {
        const parsed = JSON.parse(error.error);
        if (parsed && typeof parsed === 'object' && 'message' in parsed) {
          return parsed as ApiError;
        }
      } catch {
        // Not valid JSON, ignore
      }
    }

    return null;
  }

  /**
   * Formats an error message for user presentation
   *
   * @param message The original error message
   * @returns A formatted message for the user
   */
  #formatErrorMessage(message: string): string {
    // Remove unnecessary technical information
    let formattedMessage = message
      // Remove error codes or technical identifiers that may be at the beginning of the message
      .replace(/^\[\w+\]\s+/g, '')
      // Remove references to classes or namespaces
      .replace(/\b[\w\\]+Exception\b/g, '')
      // Ensure the message starts with a capital letter
      .trim();

    // Ensure the message ends with a period
    if (formattedMessage && !formattedMessage.endsWith('.')) {
      formattedMessage += '.';
    }

    // If after cleaning the message is empty, use a generic one
    if (!formattedMessage) {
      return GENERIC_ERROR_MESSAGE;
    }

    return formattedMessage;
  }

  /**
   * Extracts and translates validation errors from 422 responses
   *
   * @param error The HTTP error response with status 422
   * @returns A translated validation error message or null if none found
   */
  #extractValidationError(error: HttpErrorResponse): string | null {
    try {
      const errorBody = error.error;

      // Handle case where error.error exists and contains nested errors
      if (errorBody && typeof errorBody === 'object' && 'error' in errorBody) {
        const nestedError = errorBody.error;

        // Check if we have the expected structure: { errors: { field: [messages] } }
        if (
          nestedError &&
          typeof nestedError === 'object' &&
          'errors' in nestedError
        ) {
          const fieldErrors = nestedError.errors as Record<string, string[]>;

          // Extract the first error message from any field
          for (const fieldMessages of Object.values(fieldErrors)) {
            if (Array.isArray(fieldMessages) && fieldMessages.length > 0) {
              const originalMessage = fieldMessages[0];

              // Try to translate the message
              const translatedMessage =
                VALIDATION_ERROR_TRANSLATIONS[originalMessage];
              if (translatedMessage) {
                return translatedMessage;
              }

              // If no translation found, return the formatted original message
              return this.#formatErrorMessage(originalMessage);
            }
          }
        }
      }

      // Fallback: try to get message from the top level
      if (
        errorBody &&
        typeof errorBody === 'object' &&
        'message' in errorBody
      ) {
        const message = String(errorBody.message);
        const translatedMessage = VALIDATION_ERROR_TRANSLATIONS[message];
        if (translatedMessage) {
          return translatedMessage;
        }
        return this.#formatErrorMessage(message);
      }

      return null;
    } catch {
      return null;
    }
  }
}
